/**
 * Verre Jaune — Favorites / Wishlist (storefront)
 * --------------------------------------------------------------
 * - Registers the <save-button> custom element used on product cards + PDP.
 * - Instant optimistic toggle: the heart fills the moment you tap, backed by
 *   localStorage, then reconciles with the server (server is source of truth).
 * - Anonymous-first: a signed guest session (vj_wl_sid) is minted by the
 *   backend on the first save and echoed back; we persist it and send it on
 *   later calls. A logged-in customer is identified by Shopify via the proxy.
 * - Talks to the wishlist App Proxy at /apps/fire/wishlist/* — same-origin
 *   through Shopify, so there is NO CORS and no token plumbing.
 * - Private: never renders a count. The heart shows only saved / not-saved.
 *
 * Loaded globally from layout/theme.liquid via patch-layout-wishlist.js:
 *   <script src="{{ 'vj-save.js' | asset_url }}" defer></script>
 */
(function () {
  'use strict';

  var SID_KEY = 'vj_wl_sid';      // signed guest-session token
  var SAVED_KEY = 'vj_wl_saved';  // array of saved product ids (for instant paint)
  var PROXY = '/apps/fire/wishlist';

  // ---- tiny storage helpers (fail silently in private mode) ----
  function getStore(key) { try { return JSON.parse(localStorage.getItem(key)); } catch (e) { return null; } }
  function setStore(key, val) { try { localStorage.setItem(key, JSON.stringify(val)); } catch (e) {} }
  function getSid() { try { return localStorage.getItem(SID_KEY) || null; } catch (e) { return null; } }
  function setSid(t) { try { if (t) localStorage.setItem(SID_KEY, t); } catch (e) {} }

  function getSavedSet() {
    var arr = getStore(SAVED_KEY);
    var s = {};
    if (Array.isArray(arr)) { for (var i = 0; i < arr.length; i++) s[String(arr[i])] = true; }
    return s;
  }
  function persistSavedSet(set) { setStore(SAVED_KEY, Object.keys(set)); }
  function savedCount() { var arr = getStore(SAVED_KEY); return Array.isArray(arr) ? arr.length : 0; }
  // Broadcast so the header count bubble (and anything else) can react to a save.
  function dispatchChange() { try { document.dispatchEvent(new CustomEvent('vj:wishlist-changed')); } catch (e) {} }

  function prefersReducedMotion() {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
  }

  // Region for the by-market marketing cut: Shopify.country ('CA'/'US') →
  // 'ca'/'us'; else parse the locale path (/en-us legacy /en-ca, /fr-ca). Best
  // effort — never blocks a save. Bare /fr (Markets migration 2026-07-20:
  // Canada presence is domain-anchored, FR lost its -ca suffix) is the Canada
  // market's French — region 'ca'.
  function currentRegion() {
    try { if (window.Shopify && Shopify.country) return String(Shopify.country).toLowerCase(); } catch (e) {}
    var m = location.pathname.match(/^\/[a-z]{2}-([a-z]{2})(\/|$)/i);
    if (m) return m[1].toLowerCase();
    if (/^\/fr(\/|$)/i.test(location.pathname)) return 'ca';
    return null;
  }

  var registry = [];      // every <save-button> on the page
  var listLoaded = false;

  // The guest session rides as a query param (Shopify signs the whole proxied
  // query string, so this stays inside the App Proxy HMAC).
  function listUrl() {
    var sid = getSid();
    return PROXY + '/list' + (sid ? ('?session=' + encodeURIComponent(sid)) : '');
  }

  // One /list call per page → the authoritative saved set → reconcile every
  // card. localStorage already painted the optimistic state, so this only
  // corrects drift (e.g. saved on another device while logged in).
  function reconcileFromServer() {
    if (listLoaded) return;
    listLoaded = true;
    fetch(listUrl(), { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !Array.isArray(data.items)) return;
        // Guard: a server-empty response while we still hold local saves is
        // almost always an identity/timing blip (e.g. proxy didn't inject
        // logged_in_customer_id on this request). Don't zero the count — keep
        // local, just repaint. This is the main "counter disappears" cause.
        if (data.items.length === 0 && savedCount() > 0) { updateNavCount(); return; }
        var set = {};
        for (var i = 0; i < data.items.length; i++) set[String(data.items[i].id)] = true;
        persistSavedSet(set);
        dispatchChange();
        for (var j = 0; j < registry.length; j++) {
          registry[j]._render(set[registry[j]._id] === true);
        }
      })
      .catch(function () { /* keep the optimistic localStorage state */ });
  }

  function toggle(el) {
    var set = getSavedSet();
    var wasSaved = set[el._id] === true;
    var nowSaved = !wasSaved;

    // optimistic: flip UI + localStorage immediately
    if (nowSaved) set[el._id] = true; else delete set[el._id];
    persistSavedSet(set);
    dispatchChange();
    el._render(nowSaved, /*animate*/ true);

    var body = {
      product_id: parseInt(el._id, 10),
      handle: el.dataset.productHandle || null,
      region: currentRegion(),
      session: getSid() || undefined
    };

    // POST carries no client query — Shopify appends its own signed params
    // (shop / logged_in_customer_id / signature). The session goes in the body.
    fetch(PROXY + '/toggle', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
      .then(function (res) {
        if (res && res.session) setSid(res.session);  // persist a freshly-minted guest id
        // server is source of truth — correct the UI if it disagrees
        if (res && typeof res.saved === 'boolean' && res.saved !== nowSaved) {
          var s2 = getSavedSet();
          if (res.saved) s2[el._id] = true; else delete s2[el._id];
          persistSavedSet(s2);
          el._render(res.saved);
        }
      })
      .catch(function () {
        // hard failure → roll back the optimistic flip
        var s3 = getSavedSet();
        if (wasSaved) s3[el._id] = true; else delete s3[el._id];
        persistSavedSet(s3);
        dispatchChange();
        el._render(wasSaved);
      });
  }

  function defineElement() {
    if (!window.customElements || customElements.get('save-button')) return;
    customElements.define('save-button', class extends HTMLElement {
      connectedCallback() {
        var self = this;
        this._id = String(this.dataset.productId || '');
        this._btn = this.querySelector('.vj-save__btn');
        if (!this._id || !this._btn) return;

        var labelSaved = this.dataset.labelSaved || 'Saved';
        var labelSave = this.dataset.labelSave || 'Save';

        this._render = function (saved, animate) {
          self.classList.toggle('is-saved', !!saved);
          self._btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
          var lbl = self.querySelector('[data-save-label]');
          if (lbl) lbl.textContent = saved ? labelSaved : labelSave;
          if (animate && saved && !prefersReducedMotion()) {
            self.classList.remove('is-bursting');
            void self.offsetWidth;            // reflow → restart the animation
            self.classList.add('is-bursting');
          }
        };

        // instant paint from localStorage (no flash), then server reconcile
        this._render(getSavedSet()[this._id] === true);

        // Bind ONCE across reconnects (QA 2026-07-21 — same class as the
        // fire-vote double-vote fix). connectedCallback fires every time the
        // element is inserted into the DOM — a card, slider, or gallery
        // re-parenting the heart all count — so a plain addEventListener
        // stacked another listener each time, and from then on one click ran
        // toggle() twice: add-then-immediately-remove, so the save silently
        // failed to stick. Keep single stored handler references and
        // remove-then-add so binding is idempotent no matter how often the
        // element reconnects; disconnectedCallback tears them down. (The old
        // handlers were anonymous functions — impossible to removeEventListener.)
        if (!this._onClick) this._onClick = function (e) {
          e.preventDefault();
          e.stopPropagation();
          toggle(self);
        };
        if (!this._onAnimEnd) this._onAnimEnd = function () { self.classList.remove('is-bursting'); };
        this._btn.removeEventListener('click', this._onClick);
        this._btn.addEventListener('click', this._onClick);
        this.removeEventListener('animationend', this._onAnimEnd);
        this.addEventListener('animationend', this._onAnimEnd);

        // Dedupe: this registry is a PERSISTENT array (not splice-drained like
        // fire's), so a reconnect would otherwise push a duplicate and leak the
        // stale reference. disconnectedCallback removes it again.
        if (registry.indexOf(this) === -1) registry.push(this);
        reconcileFromServer();
      }

      disconnectedCallback() {
        // Detach listeners + drop from the registry when the element leaves the
        // DOM, so nothing survives on a re-parented node.
        if (this._btn && this._onClick) this._btn.removeEventListener('click', this._onClick);
        if (this._onAnimEnd) this.removeEventListener('animationend', this._onAnimEnd);
        var idx = registry.indexOf(this);
        if (idx !== -1) registry.splice(idx, 1);
      }
    });
  }

  // ---- inject a heart next to every flame (parity with Jaune Fire) ----
  // The save button is a JS-only feature (needs fetch + localStorage), so
  // building it in JS — rather than server-rendering it on ~11 card surfaces —
  // is both DRY and correct: no dead button for no-JS visitors. Every card that
  // shows the flame (<fire-vote>) gets a heart in the opposite (top-right)
  // corner. The main product grid keeps its server-rendered heart
  // (product-card.liquid); this fills in every OTHER surface — homepage hero /
  // strips / mood mosaics, PDP cross-sell / siblings / related, accessories.
  var HEART_SVG = '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path class="vj-save__heart-shape" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>';

  function heartLabels() {
    var lang = (document.documentElement.getAttribute('lang') || 'en').toLowerCase();
    var fr = lang.indexOf('fr') === 0;
    return fr
      ? { save: 'Ajouter à ta liste', saved: 'Dans ta liste', aria: 'Ajouter à ta liste de souhaits' }
      : { save: 'Add to Wishlist', saved: 'In Wishlist', aria: 'Add to wishlist' };
  }

  // Build a heart anchor from a flame element (reads its product id/handle/title
  // dataset). DOM-built + setAttribute so a title with quotes can't break markup.
  function buildHeartAnchor(fv) {
    var id = fv.getAttribute('data-product-id');
    var handle = fv.getAttribute('data-product-handle') || '';
    var title = fv.getAttribute('data-product-title') || '';
    var L = heartLabels();

    var anchor = document.createElement('div');
    anchor.className = 'vj-save-card-anchor';
    var sb = document.createElement('save-button');
    sb.className = 'vj-save';
    sb.setAttribute('data-product-id', id);
    if (handle) sb.setAttribute('data-product-handle', handle);
    sb.setAttribute('data-label-save', L.save);
    sb.setAttribute('data-label-saved', L.saved);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'vj-save__btn';
    btn.setAttribute('aria-pressed', 'false');
    btn.setAttribute('aria-label', L.aria + (title ? ': ' + title : ''));
    btn.innerHTML = '<span class="vj-save__heart" aria-hidden="true">' + HEART_SVG + '</span>';

    var spark = document.createElement('span');
    spark.className = 'vj-save__spark';
    spark.setAttribute('aria-hidden', 'true');

    sb.appendChild(btn);
    sb.appendChild(spark);
    anchor.appendChild(sb);
    return anchor;
  }

  function injectHearts(root) {
    var live = (root || document).getElementsByTagName('fire-vote');
    var flames = [];
    for (var i = 0; i < live.length; i++) flames.push(live[i]); // snapshot (we mutate the DOM)
    for (var k = 0; k < flames.length; k++) {
      var fv = flames[k];
      if (!fv.getAttribute('data-product-id')) continue;
      var fireAnchor = fv.closest ? fv.closest('.vj-fire-card-anchor') : null;
      var container = fireAnchor ? fireAnchor.parentNode : fv.parentNode;
      if (!container) continue;
      if (container.querySelector('.vj-save-card-anchor')) continue; // already present (server-rendered or injected)
      // Product-card flames live inside .card-gallery (QA 2026-07 bug #4
      // refinement: flame drops below the art on compact grids), so the
      // server-rendered heart is one level up from the flame's container —
      // check the whole card before treating the heart as missing, or every
      // grid card gets a second, injected heart.
      var cardScope = fv.closest ? fv.closest('.product-card') : null;
      if (cardScope && cardScope.querySelector('.vj-save-card-anchor')) continue;
      var anchor = buildHeartAnchor(fv);
      if (fireAnchor && fireAnchor.parentNode) {
        fireAnchor.parentNode.insertBefore(anchor, fireAnchor.nextSibling);
      } else {
        container.appendChild(anchor);
      }
    }
  }

  // Collection facet filtering / infinite scroll swap the grid via AJAX — re-scan
  // when new flames appear so injected hearts survive a filter change.
  function watchForFlames() {
    if (!window.MutationObserver || !document.body) return;
    var scheduled = false, needInject = false;
    var runInject = function () {
      scheduled = false;
      if (needInject) { needInject = false; injectHearts(document); refreshHeader(); }
    };
    var obs = new MutationObserver(function (muts) {
      // Restore the count SYNCHRONOUSLY here (observer microtask runs before the
      // browser paints). Horizon re-renders the header on scroll/sticky/cart
      // updates, resetting our server-rendered count span to its hidden default;
      // healing it in a deferred rAF let a "0/hidden" frame paint first → the
      // flicker the owner reported. Doing it synchronously means the reset frame
      // never reaches the screen. updateNavCount is idempotent → no observer loop.
      updateNavCount();
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if ((n.matches && (n.matches('fire-vote') || n.matches('.vj-save-nav'))) ||
              (n.querySelector && (n.querySelector('fire-vote') || n.querySelector('.vj-save-nav')))) {
            needInject = true;
          }
        }
      }
      if (needInject && !scheduled) { scheduled = true; (window.requestAnimationFrame || window.setTimeout)(runInject); }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  // ---- header entry: a heart link to /pages/saved with a live count bubble ----
  // The store's header bar is stock Horizon (not in our theme repo), so we can't
  // server-render into it — inject a Saved link before the cart icon. Best-effort
  // across Horizon markup variants; if none match, /pages/saved is still directly
  // reachable and the count bubble is simply absent.
  var SVG_HEART_STROKE = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>';

  function savedPageHref() {
    // Locale prefix from Shopify's own routing (Horizon sets Shopify.routes.root
    // on every page: '/' EN, '/fr/' FR) — never hardcode the prefix, it already
    // changed shape once (/fr-ca → /fr, Markets migration 2026-07-20).
    var root = ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/').replace(/\/$/, '');
    return root + '/pages/saved';
  }
  // Update ALL count bubbles (sticky-header clones → two). IDEMPOTENT: only
  // touches the DOM when a value actually changes, so it can be called on every
  // mutation (see watchForFlames) to re-affirm the count after Horizon re-renders
  // the header — without triggering an observer feedback loop.
  function updateNavCount() {
    var n = savedCount();
    var txt = n > 99 ? '99+' : String(n);
    var wantHidden = n === 0;
    var badges = document.querySelectorAll('.vj-save-nav__count');
    for (var i = 0; i < badges.length; i++) {
      var b = badges[i];
      if (b.textContent !== txt) b.textContent = txt;
      if (b.hidden !== wantHidden) b.hidden = wantHidden;
    }
  }
  // Fallback: if the header wasn't server-patched, inject a heart before the cart.
  function injectHeaderEntry() {
    if (document.querySelector('.vj-save-nav')) return;
    var cart = document.querySelector(
      'header a[href$="/cart"], .header a[href$="/cart"], [class*="header"] a[href$="/cart"], a.header__icon--cart, a[href$="/cart"]'
    );
    if (!cart || !cart.parentNode) return;
    var L = heartLabels();
    var link = document.createElement('a');
    link.className = 'vj-save-nav';
    link.href = savedPageHref();
    link.setAttribute('aria-label', L.saved);
    link.innerHTML = SVG_HEART_STROKE + '<span class="vj-save-nav__count" hidden></span>';
    cart.parentNode.insertBefore(link, cart);
  }

  // ---- wishlist quick-view panel: opens from the header heart like a mega menu,
  //      so a shopper reviews / removes favorites without leaving the page. ----
  function isFr() { return (document.documentElement.getAttribute('lang') || '').toLowerCase().indexOf('fr') === 0; }
  // From Shopify.routes.root, not a hardcoded string — see savedPageHref().
  function localePrefix() { return ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/').replace(/\/$/, ''); }
  function sizedUrl(url, w) { if (!url) return ''; if (url.indexOf('//') === 0) url = 'https:' + url; return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w; }
  function formatMoney(cents) { try { if (window.Shopify && Shopify.formatMoney) return Shopify.formatMoney(cents); } catch (e) {} return '$' + (cents / 100).toFixed(2); }

  var panelEl = null, panelOpen = false;

  function buildPanel() {
    if (panelEl) return panelEl;
    var fr = isFr();
    panelEl = document.createElement('div');
    panelEl.className = 'vj-wl-panel';
    panelEl.hidden = true;
    panelEl.innerHTML =
      '<div class="vj-wl-panel__head">' +
        '<span class="vj-wl-panel__title">' + (fr ? 'Liste de souhaits' : 'Wishlist') + '</span>' +
        '<button type="button" class="vj-wl-panel__close" aria-label="' + (fr ? 'Fermer' : 'Close') + '">&times;</button>' +
      '</div>' +
      '<div class="vj-wl-panel__grid" data-wl-grid></div>' +
      '<div class="vj-wl-panel__msg" data-wl-empty hidden>' + (fr ? 'Ta liste de souhaits est vide.' : 'Your wishlist is empty.') + '</div>' +
      '<div class="vj-wl-panel__msg" data-wl-loading>' + (fr ? 'Chargement…' : 'Loading…') + '</div>' +
      '<a class="vj-wl-panel__all" href="' + savedPageHref() + '">' + (fr ? 'Voir tout' : 'See all') + '</a>';
    document.body.appendChild(panelEl);
    panelEl.querySelector('.vj-wl-panel__close').addEventListener('click', closePanel);
    return panelEl;
  }
  function positionPanel() {
    var nav = document.querySelector('.vj-save-nav');
    if (!nav || !panelEl) return;
    var r = nav.getBoundingClientRect();
    panelEl.style.top = (r.bottom + 8) + 'px';
    panelEl.style.right = Math.max(12, Math.round(window.innerWidth - r.right - 4)) + 'px';
  }
  function openPanel() {
    buildPanel();
    panelEl.hidden = false;
    panelOpen = true;
    positionPanel();
    loadPanel();
    document.addEventListener('mousedown', onDocDown, true);
    document.addEventListener('keydown', onKeydown);
    window.addEventListener('resize', positionPanel);
  }
  function closePanel() {
    if (!panelEl) return;
    panelEl.hidden = true;
    panelOpen = false;
    document.removeEventListener('mousedown', onDocDown, true);
    document.removeEventListener('keydown', onKeydown);
    window.removeEventListener('resize', positionPanel);
  }
  function onDocDown(e) {
    if (!panelEl || panelEl.contains(e.target)) return;
    if (e.target.closest && e.target.closest('.vj-save-nav')) return;
    closePanel();
  }
  function onKeydown(e) { if (e.key === 'Escape') closePanel(); }

  function loadPanel() {
    var grid = panelEl.querySelector('[data-wl-grid]');
    var empty = panelEl.querySelector('[data-wl-empty]');
    var loading = panelEl.querySelector('[data-wl-loading]');
    grid.innerHTML = '';
    empty.hidden = true;
    loading.hidden = false;
    var sid = getSid();
    fetch(PROXY + '/list' + (sid ? ('?session=' + encodeURIComponent(sid)) : ''), { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : { items: [] }; })
      .then(function (data) {
        var items = (data && data.items) || [];
        loading.hidden = true;
        if (!items.length) { empty.hidden = false; return; }
        items.forEach(function (it) { renderPanelCard(grid, it); });
      })
      .catch(function () { loading.hidden = true; empty.hidden = false; });
  }
  function titleFromHandle(h) { return (h || '').replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); }); }
  // Fetch card data via the Section Rendering API (vj-wl-card): the PALE-caption
  // art (dark-BG legible, same as the rest of the site) + title + price.
  function fetchCard(handle, tries) {
    tries = tries || 0;
    return fetch(localePrefix() + '/products/' + handle + '?section_id=vj-wl-card', { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.text().then(function (html) { return { html: html, finalUrl: r.url }; });
      })
      .then(function (res) {
        var el = new DOMParser().parseFromString(res.html, 'text/html').querySelector('[data-vj-wl-card]');
        if (!el) throw new Error('no card data');
        // The wishlist stores the handle from whatever locale the save happened
        // in; FR handles are translated, so under the other locale this fetch
        // 301s to the localized handle. Adopt that URL for the card link so the
        // PDP link and quick-add (whose .js fetch does NOT follow translated
        // handles) hit the localized product directly.
        var url = null;
        try {
          var m = new URL(res.finalUrl, window.location.origin).pathname.match(/^(.*?\/products\/[^/?#]+)/);
          url = m ? m[1] : null;
        } catch (e) {}
        return { img: el.getAttribute('data-img') || '', mat: el.getAttribute('data-mat') === '1', title: el.getAttribute('data-title') || '', price: parseInt(el.getAttribute('data-price'), 10), url: url };
      })
      .catch(function (e) {
        if (tries < 2) return new Promise(function (res) { setTimeout(res, 400 * (tries + 1)); }).then(function () { return fetchCard(handle, tries + 1); });
        throw e;
      });
  }
  // Build the card IMMEDIATELY (never dropped on a slow/failed lookup), enrich async.
  function renderPanelCard(grid, it) {
    if (!it.handle) return;
    var prefix = localePrefix();
    var card = document.createElement('div');
    card.className = 'vj-scard';
    var a = document.createElement('a');
    a.className = 'vj-scard__link';
    a.href = prefix + '/products/' + it.handle;
    a.innerHTML = '<span class="vj-scard__frame"></span><span class="vj-scard__title"></span><span class="vj-scard__price"></span>';
    a.querySelector('.vj-scard__title').textContent = titleFromHandle(it.handle);
    var rm = document.createElement('button');
    rm.type = 'button';
    rm.className = 'vj-scard__remove';
    rm.setAttribute('aria-label', isFr() ? 'Retirer' : 'Remove');
    rm.innerHTML = '&times;';
    rm.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); removePanelItem(it, card); });
    var atc = document.createElement('button');
    atc.type = 'button';
    atc.className = 'vj-scard__atc';
    atc.textContent = isFr() ? 'Ajouter au panier' : 'Add to cart';
    atc.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); addToCart(a.href, atc); });
    card.appendChild(a);
    card.appendChild(atc);
    card.appendChild(rm);
    grid.appendChild(card);

    fetchCard(it.handle).then(function (c) {
      if (!c) return;
      if (c.url) a.href = c.url;   // localized handle (see fetchCard) — quick-add reads a.href
      if (c.img) {
        var im = document.createElement('img');   // eager: setting .src reliably loads (a dynamically-inserted lazy <img> often never does)
        im.className = c.mat ? 'vj-scard__img vj-card--light-mat' : 'vj-scard__img';   // mat (mig 101): cream field behind dark-ink raw art
        im.alt = '';
        im.src = c.img;   // already a full URL (pale art raw, or featured sized by the section)
        a.querySelector('.vj-scard__frame').appendChild(im);
      }
      if (c.title) a.querySelector('.vj-scard__title').textContent = c.title;
      if (!isNaN(c.price)) a.querySelector('.vj-scard__price').textContent = formatMoney(c.price);
    }).catch(function () { /* keep the skeleton — card still shows */ });
  }
  function removePanelItem(it, card) {
    card.style.opacity = '0.4';
    var body = { product_id: parseInt(it.id, 10), session: getSid() || undefined };
    fetch(PROXY + '/toggle', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
      .then(function (res) {
        if (res && res.session) setSid(res.session);
        var s = getSavedSet(); delete s[String(it.id)]; persistSavedSet(s); dispatchChange();
        for (var i = 0; i < registry.length; i++) { if (registry[i]._id === String(it.id)) registry[i]._render(false); }
        card.remove();
        var grid = panelEl && panelEl.querySelector('[data-wl-grid]');
        if (grid && !grid.children.length) { var em = panelEl.querySelector('[data-wl-empty]'); if (em) em.hidden = false; }
      })
      .catch(function () { card.style.opacity = ''; });
  }

  // Wire every header heart to open the panel (desktop) instead of navigating.
  // Small screens keep the plain link to /pages/saved (a dropdown is awkward
  // there). Idempotent — safe to re-run for sticky-header clones.
  function wireHeaderToggles() {
    var navs = document.querySelectorAll('.vj-save-nav');
    for (var i = 0; i < navs.length; i++) {
      if (navs[i]._wlWired) continue;
      navs[i]._wlWired = true;
      navs[i].addEventListener('click', function (e) {
        if (window.matchMedia && window.matchMedia('(max-width: 749px)').matches) return; // mobile → navigate
        e.preventDefault();
        if (panelOpen) closePanel(); else openPanel();
      });
    }
  }

  // Header-owned count fetch: seed the count from the server on load, so it's
  // correct on a fresh device and on the Saved page (where no card triggers a
  // reconcile). Same empty-guard as reconcile.
  function fetchNavCount() {
    var sid = getSid();
    fetch(PROXY + '/list' + (sid ? ('?session=' + encodeURIComponent(sid)) : ''), { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !Array.isArray(data.items)) return;
        if (data.items.length === 0 && savedCount() > 0) { updateNavCount(); return; }
        var set = {};
        for (var i = 0; i < data.items.length; i++) set[String(data.items[i].id)] = true;
        persistSavedSet(set);
        updateNavCount();
      })
      .catch(function () {});
  }

  // Add to cart — reuse the site's quick-add modal (variant picker) when present.
  function addToCart(productUrl, btn) {
    if (window.VJCart && typeof window.VJCart.quickAdd === 'function') { window.VJCart.quickAdd(productUrl, btn); }
    else { window.location.href = productUrl; }
  }

  function refreshHeader() {
    injectHeaderEntry();
    wireHeaderToggles();
    updateNavCount();
  }

  function init() {
    defineElement();
    refreshHeader();
    fetchNavCount();
    injectHearts(document);
    watchForFlames();
    document.addEventListener('vj:wishlist-changed', updateNavCount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
